'use client';

import { useEffect, useMemo } from 'react';
import * as Blockly from 'blockly/core';
import { motion } from 'framer-motion';
import {
    Blocks,
    ChartColumn,
    CloudCheck,
    CloudLightning,
    FileCode,
    Moon,
    Settings,
    SquareCode,
    Sun,
    UploadCloud,
} from 'lucide-react';
import { useBoardEditorSaveFile } from '@/hooks/feature/boards/useBoardEditorSaveFile';
import { useDeviceType } from '@/hooks/useIsMobile';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBoardEditorConfigStore } from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { useModalConfirmStore } from '@/stores/useModalConfirmStore';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import IconAdaptive from '@/components/IconAdaptive';
import TruncateText from '@/components/TruncateText';
import BoardCodeFileList from '@/(routers)/boards/editor/components/modal/BoardCodeFileList';
import BoardEditorSettings from '@/(routers)/boards/editor/components/modal/BoardEditorSettings';
import BoardEditorUsageData from '@/(routers)/boards/editor/components/modal/BoardEditorUsageData';
import BoardMyBoards from '@/(routers)/boards/editor/components/modal/BoardMyBoardList';

type BoardEditorHeaderProps = {
    onOpenFileList?: () => void;
};

const getThemeStyles = (isDark: boolean) => ({
    bg: isDark ? 'bg-black border-gray-700' : 'bg-gray-50 border-gray-200',
    text: isDark ? 'text-gray-200' : 'text-gray-700',
    hover: isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100',
    toggleBg: isDark ? 'bg-gray-900 ring-1 ring-gray-700' : 'bg-white ring-1 ring-gray-300',
    toggleSlider: isDark ? 'bg-color-white-base' : 'bg-color-ide-header-button',
    toggleActiveText: isDark ? 'text-color-base' : 'text-color-white-base',
    toggleInactiveText: isDark ? 'text-gray-200' : 'text-gray-400',
    leftBtn: isDark
        ? 'bg-black text-white hover:bg-gray-800'
        : 'bg-color-ide-header-button text-white hover:bg-opacity-80',
    themeBtn: isDark
        ? 'bg-white text-gray-900 hover:bg-gray-100'
        : 'bg-color-ide-header-button text-white hover:bg-opacity-80',
});

const TABS = [
    { mode: 'block' as const, Icon: Blocks, label: 'ブロック' },
    { mode: 'code' as const, Icon: SquareCode, label: 'コード' },
];

export default function BoardEditorHeader({ onOpenFileList }: BoardEditorHeaderProps) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const { isDark, toggleTheme } = useBoardEditorConfigStore();
    const { editorMode, setEditorMode, savedFileId } = useBoardEditorStore();
    const { files, currentFileId, updateFile } = useBoardEditorFileStore();
    const { openModalConfirm } = useModalConfirmStore();
    const { openModalSlide, closeModalSlide } = useModalSlideStore();
    const { isDesktop } = useDeviceType();
    const { save } = useBoardEditorSaveFile();

    const currentFile = files.find((f) => f.id === currentFileId);
    const fileName = currentFile?.name || '';
    const isDirty = currentFile?.isDirty || false;
    const isSaving = currentFile?.isSaving || false;
    const isSavedVisual = !!currentFileId && savedFileId === currentFileId;
    const isSaved = currentFile?.isSaved && !isDirty && !isSavedVisual;

    const activeIndex = TABS.findIndex((t) => t.mode === editorMode);
    const theme = useMemo(() => getThemeStyles(isDark), [isDark]);

    useEffect(() => {
        const isMac = /macintosh|mac os/i.test(window.navigator.userAgent);
        const handleKeyDown = (e: KeyboardEvent) => {
            const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
            if (cmdOrCtrl && e.key === 's') {
                e.preventDefault();
                e.stopPropagation();
                save();
            }
        };
        window.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
    }, [save]);

    const handleOpenFileList = () =>
        openModalSlide({
            id: 'board-code-file-list',
            component: <BoardCodeFileList onClose={() => closeModalSlide()} />,
            size: { pcWidth: '900px', mbWidth: '100%', height: '100%' },
            direction: 'right',
        });

    const handleOpenEditorSettings = () =>
        openModalSlide({
            id: 'board-editor-settings',
            component: <BoardEditorSettings onClose={() => closeModalSlide()} />,
            size: { pcWidth: '900px', mbWidth: '100%', height: '100%' },
            direction: 'right',
        });

    const handleOpenMyBoards = () =>
        openModalSlide({
            id: 'board-my-boards',
            component: <BoardMyBoards onClose={() => closeModalSlide()} />,
            size: { pcWidth: '900px', mbWidth: '100%', height: '100%' },
            direction: 'right',
        });

    const handleOpenUsageData = () =>
        openModalSlide({
            id: 'board-editor-usage-data',
            component: <BoardEditorUsageData onClose={() => closeModalSlide()} />,
            size: { pcWidth: '900px', mbWidth: '100%', height: '100%' },
            direction: 'right',
        });

    const handleModeSwitch = (mode: 'code' | 'block') => {
        if (mode === editorMode) return;
        if (mode === 'code') {
            (Blockly as any).WidgetDiv?.hide?.();
            if (!currentFile?.isCodeEditConfirmed) {
                openModalConfirm({
                    id: 'block-to-code',
                    message: 'コードを直接編集するとブロックはロックされます',
                    subMessage:
                        'コードの閲覧のみであればロックされません。\nロックを解除してブロックのプログラムに戻すことができます。\nロック中もボードへの書き込みは可能です。',
                    confirmLabel: '切り替える',
                    cancelLabel: 'キャンセル',
                    variant: 'warning',
                    onConfirm: () => {
                        if (currentFileId) updateFile(currentFileId, { isCodeEditConfirmed: true });
                        setEditorMode('code');
                    },
                });
                return;
            }
            setEditorMode('code');
            return;
        }
        setEditorMode(mode);
    };

    const showSaveIndicator = isDirty || isSaving || isSavedVisual || isSaved;

    const SIDEBAR_BUTTONS = [
        {
            Icon: FileCode,
            label: 'ファイル管理',
            action: handleOpenFileList,
            requiresAuth: true,
            requiresDesktop: false,
        },
        {
            Icon: ChartColumn,
            label: '利用データ',
            action: handleOpenUsageData,
            requiresAuth: true,
            requiresDesktop: false,
        },
        {
            Icon: Settings,
            label: 'エディター管理',
            action: handleOpenEditorSettings,
            requiresAuth: false,
            requiresDesktop: false,
        },
    ];

    return (
        <div className={`h-[45px] ${theme.bg} border-b flex items-center px-2 flex-shrink-0 select-none gap-1`}>
            {/* 左: サイドバーボタン群 */}
            <div className="flex items-center gap-2 flex-shrink-0">
                {SIDEBAR_BUTTONS.filter(
                    ({ requiresAuth, requiresDesktop }) =>
                        (!requiresAuth || isAuthenticated) && (!requiresDesktop || isDesktop),
                ).map(({ Icon, label, action }) => (
                    <div key={label} className="relative group">
                        <button
                            onClick={action}
                            aria-label={label}
                            className={`w-9 h-9 flex items-center justify-center rounded ${theme.leftBtn} transition-colors`}
                        >
                            <IconAdaptive Icon={Icon} strokeWidth={1.5} baseSize={24} breakpoints={{ lg: 24 }} />
                        </button>
                        <div className="absolute left-0 top-full mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap invisible group-hover:visible z-50">
                            {label}
                        </div>
                    </div>
                ))}
                <div className="relative group">
                    <button
                        onClick={toggleTheme}
                        aria-label={isDark ? 'ライトモード' : 'ダークモード'}
                        className={`w-9 h-9 flex items-center justify-center rounded ${theme.themeBtn} transition-colors`}
                    >
                        <IconAdaptive
                            Icon={isDark ? Sun : Moon}
                            strokeWidth={1.5}
                            baseSize={24}
                            breakpoints={{ lg: 24 }}
                        />
                    </button>
                    <div className="absolute left-0 top-full mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap invisible group-hover:visible z-50">
                        {isDark ? 'ライトモード' : 'ダークモード'}
                    </div>
                </div>
            </div>

            {/* 中央: スライドトグル */}
            <div className="flex-1 flex justify-center">
                <div className={`relative flex items-center ${theme.toggleBg} rounded-md p-0.5`}>
                    <motion.div
                        className={`absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-4px)] rounded shadow-sm ${
                            editorMode === 'block'
                                ? 'bg-color-ide-editor-label-block'
                                : 'bg-color-ide-editor-label-code'
                        }`}
                        animate={{ x: activeIndex === 0 ? 0 : 'calc(100% + 4px)' }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                    />
                    {TABS.map(({ mode, Icon, label }) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => handleModeSwitch(mode)}
                            className={`relative z-10 flex items-center gap-1 px-2 md:px-6 lg:px-10 xl:px-16 py-1 rounded transition-colors ${
                                editorMode === mode ? 'text-white' : theme.toggleInactiveText
                            }`}
                        >
                            <IconAdaptive
                                strokeWidth={1.5}
                                Icon={Icon}
                                baseSize={22}
                                breakpoints={{ md: 22, lg: 22 }}
                            />
                            <span className="hidden md:inline font-corp font-medium text-sm ml-2">{label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* 右: ファイル名 + 保存状態 */}
            <div className="flex items-center justify-end gap-1 flex-shrink-0">
                {isAuthenticated && showSaveIndicator && (
                    <div className="hidden lg:flex items-center justify-center w-5 flex-shrink-0">
                        <motion.button
                            onClick={isDirty ? save : undefined}
                            disabled={!isDirty}
                            animate={isDirty || isSaving ? { opacity: [1, 0.7, 1] } : {}}
                            transition={isDirty || isSaving ? { duration: 2, repeat: Infinity, ease: 'easeInOut' } : {}}
                            className={isDirty ? 'cursor-pointer' : 'cursor-default'}
                            title={
                                isDirty ? '保存する' : isSaving ? '保存中...' : isSavedVisual ? '保存完了' : '保存済み'
                            }
                        >
                            {isDirty && (
                                <IconAdaptive
                                    Icon={UploadCloud}
                                    baseSize={22}
                                    className="text-color-ide-file-discard-button"
                                />
                            )}
                            {isSaving && (
                                <motion.div
                                    animate={{ opacity: [1, 0.3, 1] }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                                >
                                    <IconAdaptive
                                        Icon={CloudLightning}
                                        baseSize={22}
                                        className="text-color-ide-file-saved"
                                    />
                                </motion.div>
                            )}
                            {!isDirty && !isSaving && isSavedVisual && (
                                <IconAdaptive Icon={CloudCheck} baseSize={22} className="text-color-ide-file-saved" />
                            )}
                            {!isDirty && !isSaving && isSaved && (
                                <IconAdaptive Icon={CloudCheck} baseSize={22} className="text-gray-400" />
                            )}
                        </motion.button>
                    </div>
                )}
                {isAuthenticated && isDesktop && (
                    <button
                        onClick={() => onOpenFileList?.()}
                        className={`flex items-center gap-2 ${theme.hover} px-2 py-1 rounded transition-colors`}
                    >
                        <span className={`hidden lg:inline text-xs font-cantarell ${theme.text}`}>
                            <TruncateText text={fileName} maxLength={15} breakpoints={{ lg: 20, xl: 40, '2xl': 999 }} />
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
}
